/* eslint-disable react/prop-types */
import cx from "classnames";
import { useState, useRef } from "react";

import DebouncedFrame from "metabase/components/DebouncedFrame";
import CS from "metabase/css/core/index.css";
import QueryVisualization from "metabase/query_builder/components/QueryVisualization";
import { SyncedParametersList } from "metabase/query_builder/components/SyncedParametersList";
import { TimeseriesChrome } from "metabase/querying/filters/components/TimeseriesChrome";
import { Box } from "metabase/ui";
import * as Lib from "metabase-lib";

import { ViewFooter } from "../../ViewFooter";
import { ViewNativeQueryEditor } from "../ViewNativeQueryEditor";
import ChatBotPanel from "./ChatBotPanel";

import ViewMainContainerS from "./ViewMainContainer.module.css";

export const ViewMainContainer = (props) => {
  const {
    queryBuilderMode,
    mode,
    question,
    showLeftSidebar,
    showRightSidebar,
    parameters,
    setParameterValue,
    isLiveResizable,
    updateQuestion,
  } = props;

  if (queryBuilderMode === "notebook") {
    return;
  }

  const queryMode = mode && mode.queryMode();
  const { isNative } = Lib.queryDisplayInfo(question.query());
  const isSidebarOpen = showLeftSidebar || showRightSidebar;

  const [chatPanelWidth, setChatPanelWidth] = useState(() => {
    if (typeof window !== "undefined") {
      const totalWidth = window.innerWidth;
      const maxAllowed = 600; // same as your maxWidth cap during resize
      return Math.min(totalWidth * 0.7, maxAllowed);
    }
    return 600; // fallback
  });
  
  const containerRef = useRef(null);

  return (
    <Box
      component="main"
      className={cx(ViewMainContainerS.QueryBuilderMain, {
        [ViewMainContainerS.isSidebarOpen]: isSidebarOpen,
      })}
      data-testid="query-builder-main"
    >
      <div
        ref={containerRef}
        style={{
          display: "flex",
          gap: "0",
          height: "100%",
        }}
      >
        {/* LEFT PANEL: Chat */}
        <div
          style={{
            width: chatPanelWidth,
            display: "flex",
            flexDirection: "column",
            maxHeight: "80vh",
            overflow: "hidden",
            minWidth: "200px",
          }}
        >
          <ChatBotPanel
            getCurrentSQL={() => question?.query()?.rawQuery?.() ?? ""}
            setSQLQuery={(newSQL) => {
              const sqlString = newSQL?.native?.query;

              if (typeof sqlString !== "string" || sqlString.trim() === "") {
                console.warn("🚫 Invalid SQL string passed to setSQLQuery:", sqlString);
                return;
              }

              props.setDatasetQuery?.({
                type: "native",
                native: {
                  query: sqlString,
                  template_tags: {},
                },
              });

              setTimeout(() => {
                props.runQuestionQuery?.();
              }, 3500);
            }}
          />
        </div>

        {/* RESIZER HANDLE */}
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            const containerLeft = containerRef.current.getBoundingClientRect().left;

            const handleMouseMove = (e) => {
              const newWidth = e.clientX - containerLeft;
              const minWidth = 200;
              const maxWidth = 600;
              setChatPanelWidth(Math.max(minWidth, Math.min(newWidth, maxWidth)));
            };

            const handleMouseUp = () => {
              document.removeEventListener("mousemove", handleMouseMove);
              document.removeEventListener("mouseup", handleMouseUp);
            };

            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
          }}
          style={{
            width: "5px",
            cursor: "col-resize",
            background: "#ccc",
            zIndex: 1,
          }}
        />

        {/* RIGHT PANEL: Editor and Results */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            minWidth: 0, // <-- CRITICAL: allows it to shrink properly
            overflow: "hidden", // prevents unbounded growth
          }}
        >

          {isNative ? (
            <ViewNativeQueryEditor {...props} />
          ) : (
            <SyncedParametersList
              className={ViewMainContainerS.StyledSyncedParametersList}
              parameters={parameters}
              setParameterValue={setParameterValue}
              commitImmediately
            />
          )}

          <DebouncedFrame
            className={ViewMainContainerS.StyledDebouncedFrame}
            enabled={!isLiveResizable}
          >
            <QueryVisualization
              {...props}
              noHeader
              className={CS.spread}
              mode={queryMode}
              onUpdateQuestion={updateQuestion}
            />
          </DebouncedFrame>

          <TimeseriesChrome
            question={question}
            updateQuestion={updateQuestion}
            className={CS.flexNoShrink}
          />

          <ViewFooter className={CS.flexNoShrink} />
        </div>
      </div>
    </Box>
  );
};
